/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ApolloError } from '@apollo/client'
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material'
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { GetTenantStoresSimpleQuery, GroupFragment } from '@/graphql'
import useT from '@/i18n/useT'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import { EditableUserRow, groupRequiresEmail } from './ImportUsersCsvDialog'

type Store = GetTenantStoresSimpleQuery['tenantStores']['results'][number] & {
  location: NonNullable<
    GetTenantStoresSimpleQuery['tenantStores']['results'][number]['location']
  >
}

const formatStoreAddress = (location: Store['location']) =>
  [
    [location.address1, location.address2].filter(Boolean).join(' '),
    [location.city, location.state].filter(Boolean).join(', '),
    location.postalCode,
  ]
    .filter(Boolean)
    .join(', ')

interface Props {
  rows: EditableUserRow[]
  rowValidation: (row: EditableUserRow) => {
    nameError: string | null
    roleError: string | null
    storeError: string | null
    pinError: string | null
    emailError: string | null
    isAdmin: boolean
  }
  updateRow: (key: string, changes: Partial<EditableUserRow>) => void
  groups: GroupFragment[]
  groupsLoading: boolean
  groupsError: ApolloError | undefined
  adminGroup: GroupFragment | undefined
  stores: Store[]
  storesLoading: boolean
}

const ImportUsersCsvTable: React.FC<Props> = ({
  rows,
  rowValidation,
  updateRow,
  groups,
  groupsLoading,
  groupsError,
  adminGroup,
  stores,
  storesLoading,
}) => {
  const { t } = useT()

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('importUsersCsv.table.name', 'Name')}</TableCell>
            <TableCell>{t('importUsersCsv.table.role', 'Role')}</TableCell>
            <TableCell>
              {t('importUsersCsv.table.store', 'Assigned Store')}
            </TableCell>
            <TableCell>{t('importUsersCsv.table.pin', 'Pin')}</TableCell>
            <TableCell>{t('importUsersCsv.table.email', 'Email')}</TableCell>
            <TableCell>{t('importUsersCsv.table.status', 'Status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const {
              nameError,
              roleError,
              storeError,
              pinError,
              emailError,
              isAdmin,
            } = rowValidation(row)
            return (
              <TableRow key={row.key}>
                <TableCell sx={{ minWidth: 160 }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.name}
                    error={Boolean(nameError)}
                    helperText={nameError}
                    disabled={row.created}
                    onChange={e =>
                      updateRow(row.key, {
                        name: e.target.value,
                        nameError: null,
                      })
                    }
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    error={Boolean(roleError)}
                    disabled={row.created || groupsLoading}
                    value={row.roleId}
                    onChange={e => {
                      const roleId = e.target.value
                      updateRow(row.key, {
                        roleId,
                        roleError: null,
                        ...(roleId === adminGroup?.id
                          ? { storeId: '', storeError: null }
                          : {}),
                      })
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t('importUsersCsv.selectRole', 'Select a role')}
                    </MenuItem>
                    {groupsError ? (
                      <MenuItem value="" disabled>
                        <Typography color="error">
                          {groupsError.message}
                        </Typography>
                      </MenuItem>
                    ) : (
                      groups.map(group => {
                        const disabledForMissingEmail =
                          groupRequiresEmail(group) && !row.email.trim()
                        return (
                          <MenuItem
                            key={group.id}
                            value={group.id}
                            disabled={disabledForMissingEmail}
                          >
                            <Stack>
                              <Typography variant="body2">
                                {LocalizationHelpers.localizedGroupName(
                                  t,
                                  group.name,
                                )}
                              </Typography>
                              {disabledForMissingEmail && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t(
                                    'importUsersCsv.roleRequiresEmailHint',
                                    'Requires an email address',
                                  )}
                                </Typography>
                              )}
                            </Stack>
                          </MenuItem>
                        )
                      })
                    )}
                  </Select>
                  {roleError && (
                    <Typography variant="caption" color="error">
                      {roleError}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  {isAdmin ? (
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        'importUsersCsv.noStoreNeeded',
                        'Not required for this role',
                      )}
                    </Typography>
                  ) : (
                    <>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        error={Boolean(storeError)}
                        disabled={row.created || storesLoading}
                        value={row.storeId}
                        onChange={e =>
                          updateRow(row.key, {
                            storeId: e.target.value,
                            storeError: null,
                          })
                        }
                        renderValue={selected => {
                          const selectedStore = stores.find(
                            store => store.id === selected,
                          )
                          if (!selectedStore) {
                            return t(
                              'importUsersCsv.selectStore',
                              'Select a store',
                            )
                          }
                          return (
                            <Box>
                              <Typography variant="body2">
                                {selectedStore.location.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatStoreAddress(selectedStore.location)}
                              </Typography>
                            </Box>
                          )
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('importUsersCsv.selectStore', 'Select a store')}
                        </MenuItem>
                        {stores.map(store => (
                          <MenuItem
                            key={store.id}
                            value={store.id}
                            sx={{ display: 'block' }}
                          >
                            <Typography variant="body2">
                              {store.location.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatStoreAddress(store.location)}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      {storeError && (
                        <Typography variant="caption" color="error">
                          {storeError}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.pin}
                    error={Boolean(pinError)}
                    helperText={pinError}
                    disabled={row.created}
                    onChange={e => {
                      const value = e.target.value
                      if (/^\d{0,10}$/.test(value)) {
                        updateRow(row.key, {
                          pin: value,
                          pinError: null,
                        })
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={row.email}
                    error={Boolean(emailError)}
                    helperText={emailError}
                    disabled={row.created}
                    onChange={e =>
                      updateRow(row.key, {
                        email: e.target.value,
                        emailError: null,
                      })
                    }
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {row.created ? (
                    <Tooltip
                      title={t('importUsersCsv.status.created', 'Created')}
                    >
                      <CheckCircle color="success" fontSize="small" />
                    </Tooltip>
                  ) : row.submitError ? (
                    <Tooltip title={row.submitError}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ErrorIcon color="error" fontSize="small" />
                        <Typography variant="caption" color="error" noWrap>
                          {row.submitError}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ImportUsersCsvTable
